<template>
	<div>
		<v-card class="supplier-performance-intro-card pa-6 mb-4" flat outlined>
			<p class="supplier-performance-intro-text mb-0">
				Explore your suppliers' performance in detail under this tab. This overview provides a clear view of supplier performance based on the latest Quality, Delivery, and Support KPI scores. The overall supplier performance score is calculated as the average of the supplier's three latest KPI scores, providing a consolidated view of their current performance. This overview helps you identify high-performing suppliers, highlight areas requiring attention, and proactively manage supplier relationships and upcoming contract expirations.
			</p>
			<p class="supplier-performance-intro-text supplier-performance-intro-hint mt-4 mb-0">
				Select a supplier name to access the complete connection profile, including contract status, sustainability performance, and related supplier information.
			</p>
			<div class="supplier-performance-sort d-flex align-center mt-6">
				<h3 class="supplier-performance-sort-label mb-0 mr-4">Sort by :</h3>
				<v-select
					v-model="selectedSort"
					:items="dropdownItems"
					item-text="title"
					item-value="title"
					placeholder="Select sorting criteria"
					dense
					outlined
					hide-details
					style="width: 220px; flex: none;"
					class="supplier-performance-sort-select"
					@change="handleSortChange"
				></v-select>
			</div>
		</v-card>

		<v-card class="supplier-performance-table-card" flat outlined>
			<v-card-title class="supplier-performance-search-title">
				<v-text-field
					v-model="search"
					label="Search"
					single-line
					hide-details
					class="supplier-performance-search"
				></v-text-field>
			</v-card-title>
			<v-data-table
				theme--light
				:headers="headers"
				:items="suppliers"
				item-key="clientID"
				:search="search"
				:items-per-page="10"
				class="supplier-performance-table light table"
				:no-data-text="emptyTableMessage"
				:footer-props="{
					'items-per-page-options': [10, 15, 20],
				}"
			>
				<template v-slot:item.name="{ item }">
					<router-link
						:to="{
							name: 'Single Client View',
							params: { id: item.clientID },
							query: { focus: 'contract' },
						}"
						class="supplier-link"
					>
						{{ item.name }}
					</router-link>
				</template>

				<template v-slot:item.qualityKpi="{ item }">
					<div class="table-score-cell table-score-cell--start">
						<score-badge :value="item.qualityKpi" />
					</div>
				</template>

				<template v-slot:item.deliveryKpi="{ item }">
					<div class="table-score-cell table-score-cell--start">
						<score-badge :value="item.deliveryKpi" />
					</div>
				</template>

				<template v-slot:item.supportKpi="{ item }">
					<div class="table-score-cell table-score-cell--start">
						<score-badge :value="item.supportKpi" />
					</div>
				</template>

				<template v-slot:item.overall="{ item }">
					<div class="table-score-cell table-score-cell--start">
						<span
							v-if="item.overallStyle"
							class="average-badge"
							:style="{
								backgroundColor: item.overallStyle.pillBg,
								color: item.overallStyle.textColor,
								borderColor: item.overallStyle.borderColor,
							}"
						>{{ item.overallFormatted }}</span>
						<span v-else class="score-empty">—</span>
					</div>
				</template>

				<template v-slot:item.evaluationDate="{ item }">
					<div class="table-center-cell table-center-cell--start evaluation-date-cell">
						<span>{{ item.evaluationDateFormatted }}</span>
					</div>
				</template>

				<template v-slot:item.expiryDate="{ item }">
					<div class="table-center-cell table-center-cell--start">
						<span :class="{ 'expiry-date--expired': item.isExpired }">
							{{ item.expiryDateFormatted }}
						</span>
					</div>
				</template>
			</v-data-table>
		</v-card>
	</div>
</template>

<script>
import SupplierAPI from "../api/csr/supplier"
import ConnectionAPI from "../api/connections/connection"
import LoadingState from "../consts/LoadingState"
import {
	calculateKpiMean,
	formatKpiScore,
	getKpiScoreBadgeStyle,
	getOverallPerformanceStyle,
} from "../utils/supplierKpiOverall"

const ScoreBadge = {
	functional: true,
	props: {
		value: {
			type: [Number, String],
			default: null,
		},
	},
	render(h, { props }) {
		const style = getKpiScoreBadgeStyle(props.value)
		const formatted = formatKpiScore(props.value)
		if (!style) {
			return h("span", { class: "score-empty" }, formatted)
		}
		return h(
			"span",
			{
				class: "average-badge",
				style: {
					backgroundColor: style.pillBg,
					color: style.textColor,
					borderColor: style.borderColor,
				},
			},
			formatted,
		)
	},
}

export default {
	name: "SupplierPerformanceTable",
	components: {
		ScoreBadge,
	},
	data() {
		return {
			search: "",
			suppliers: [],
			allSuppliers: [],
			connections: [],
			selectedSort: "Total score",
			dropdownItems: [
				{ title: "Total score", action: this.sortByTotalScore },
				{ title: "Overall less than 2.5", action: this.sortByOverallLow },
				{ title: "Overall more than 3.5", action: this.sortByOverallHigh },
				{ title: "Quality less than 2.5", action: this.sortByQualityLow },
				{ title: "Quality more than 3.5", action: this.sortByQualityHigh },
				{ title: "Delivery less than 2.5", action: this.sortByDeliveryLow },
				{ title: "Delivery more than 3.5", action: this.sortByDeliveryHigh },
				{ title: "Support less than 2.5", action: this.sortBySupportLow },
				{ title: "Support more than 3.5", action: this.sortBySupportHigh },
			],
			headers: [
				{ text: "Supplier", align: "start", value: "name", sortable: false },
				{ text: "Quality", value: "qualityKpi", sortable: false, align: "start" },
				{ text: "Delivery", value: "deliveryKpi", sortable: false, align: "start" },
				{ text: "Support", value: "supportKpi", sortable: false, align: "start" },
				{ text: "Overall", value: "overall", sortable: false, align: "start" },
				{ text: "ev. date", value: "evaluationDate", sortable: false, align: "start" },
				{ text: "Exp. date", value: "expiryDate", sortable: false, align: "start" },
			],
		}
	},
	computed: {
		emptyTableMessage() {
			if (this.$store.state.loading.error) {
				return "Could not load suppliers. Close the error message below and refresh the page."
			}
			return "No suppliers found. Connect with suppliers under Connections first."
		},
	},
	mounted() {
		this.$store.dispatch("loading/RESET_ERROR", { root: true })
		this.loadSuppliers()
	},
	methods: {
		connectionName(entry) {
			return entry?.name || entry?.fromName || entry?.toName || "—"
		},
		loadSuppliers() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true },
			)

			Promise.allSettled([
				ConnectionAPI.getConnections(),
				SupplierAPI.getSupplierPerformanceOverview(),
			])
				.then(([connectionsResult, overviewResult]) => {
					const connections = connectionsResult.status === "fulfilled" && Array.isArray(connectionsResult.value)
						? connectionsResult.value
						: []
					const overview = overviewResult.status === "fulfilled" && Array.isArray(overviewResult.value)
						? overviewResult.value
						: []

					if (connections.length > 0) {
						this.applyConnectionSuppliers(connections)
						if (overview.length > 0) {
							this.mergePerformanceOverview(overview)
						}
						return
					}

					if (overview.length > 0) {
						this.applyPerformanceOverview(overview)
						return
					}

					this.suppliers = []
					this.allSuppliers = []

					const rejected = [connectionsResult, overviewResult].find(
						(result) => result.status === "rejected",
					)
					if (rejected) {
						throw rejected.reason
					}
				})
				.catch((error) => {
					this.suppliers = []
					this.allSuppliers = []
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true },
					)
				})
				.finally(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true },
					)
				})
		},
		applyPerformanceOverview(overview) {
			const rows = overview.map((entry) =>
				this.buildSupplierRow({
					clientID: entry.clientID,
					name: entry.name,
					deliveryKpi: entry.deliveryKpi ?? null,
					qualityKpi: entry.qualityKpi ?? null,
					supportKpi: entry.supportKpi ?? null,
					evaluationDate: entry.evaluationDate ?? null,
					evaluationCount: entry.evaluationCount ?? 0,
					expiryDate: entry.expiryDate ?? null,
				}),
			)
			this.allSuppliers = rows
			this.suppliers = [...rows]
			this.sortByTotalScore()
		},
		mergePerformanceOverview(overview) {
			const performanceByClientId = new Map(
				overview.map((row) => [Number(row.clientID), row]),
			)

			this.allSuppliers = this.allSuppliers.map((supplier) => {
				const performance = performanceByClientId.get(Number(supplier.clientID))
				if (!performance) {
					return supplier
				}

				return this.buildSupplierRow({
					clientID: supplier.clientID,
					name: supplier.name,
					deliveryKpi: performance.deliveryKpi ?? null,
					qualityKpi: performance.qualityKpi ?? null,
					supportKpi: performance.supportKpi ?? null,
					evaluationDate: performance.evaluationDate ?? null,
					evaluationCount: performance.evaluationCount ?? 0,
					expiryDate: performance.expiryDate ?? null,
				})
			})
			this.suppliers = [...this.allSuppliers]
			this.sortByTotalScore()
		},
		applyConnectionSuppliers(connections) {
			this.connections = []
			for (const entry of connections) {
				this.connections.push({
					clientID: entry.clientID,
					name: entry.name,
				})
			}

			const rows = this.connections.map((entry) =>
				this.buildSupplierRow({
					clientID: entry.clientID,
					name: entry.name,
					deliveryKpi: null,
					qualityKpi: null,
					supportKpi: null,
					evaluationDate: null,
					evaluationCount: 0,
					expiryDate: null,
				}),
			)
			this.allSuppliers = rows
			this.suppliers = [...rows]
			this.sortByTotalScore()
		},
		buildSupplierRow(entry) {
			const clientID = entry.clientID
			const name = this.connectionName(entry)
			const deliveryKpi = entry.deliveryKpi ?? null
			const qualityKpi = entry.qualityKpi ?? null
			const supportKpi = entry.supportKpi ?? null
			const overall = calculateKpiMean(deliveryKpi, qualityKpi, supportKpi)
			const expiryDate = entry.expiryDate || null
			const evaluationDate = entry.evaluationDate || null

			let evaluationDateFormatted = "—"
			let expiryDateFormatted = "—"
			let isExpired = false

			if (evaluationDate) {
				const evaluationMoment = this.$moment(evaluationDate)
				if (evaluationMoment.isValid()) {
					evaluationDateFormatted = evaluationMoment.format("MMM YYYY")
				}
			}

			if (expiryDate) {
				const expiryMoment = this.$moment.utc(expiryDate)
				if (expiryMoment.isValid()) {
					expiryDateFormatted = expiryMoment.format("MMM YYYY")
					isExpired = expiryMoment
						.startOf("day")
						.isBefore(this.$moment.utc().startOf("day"))
				}
			}

			const overallStyle = getOverallPerformanceStyle(deliveryKpi, qualityKpi, supportKpi)

			return {
				clientID,
				name,
				deliveryKpi,
				qualityKpi,
				supportKpi,
				overall,
				overallFormatted: overall !== null ? overall.toFixed(1) : "—",
				overallStyle: overall !== null ? overallStyle : null,
				evaluationDate,
				evaluationDateFormatted,
				evaluationCount: entry.evaluationCount || 0,
				expiryDate,
				expiryDateFormatted,
				isExpired,
			}
		},
		getSortScore(supplier, field) {
			const value = supplier[field]
			if (value === null || value === undefined || value === "") {
				return null
			}
			const parsed = Number(value)
			return Number.isFinite(parsed) ? parsed : null
		},
		handleSortChange(selectedTitle) {
			const selectedItem = this.dropdownItems.find(
				(item) => item.title === selectedTitle,
			)
			if (selectedItem) {
				selectedItem.action()
			}
		},
		sortByTotalScore() {
			this.suppliers = [...this.allSuppliers].sort((a, b) => {
				return (b.overall ?? -1) - (a.overall ?? -1)
			})
		},
		sortByOverallLow() {
			this.suppliers = this.allSuppliers.filter(
				(s) => s.overall !== null && s.overall < 2.5,
			)
		},
		sortByOverallHigh() {
			this.suppliers = this.allSuppliers.filter(
				(s) => s.overall !== null && s.overall > 3.5,
			)
		},
		sortByQualityLow() {
			this.suppliers = this.allSuppliers.filter((s) => {
				const score = this.getSortScore(s, "qualityKpi")
				return score !== null && score < 2.5
			})
		},
		sortByQualityHigh() {
			this.suppliers = this.allSuppliers.filter((s) => {
				const score = this.getSortScore(s, "qualityKpi")
				return score !== null && score > 3.5
			})
		},
		sortByDeliveryLow() {
			this.suppliers = this.allSuppliers.filter((s) => {
				const score = this.getSortScore(s, "deliveryKpi")
				return score !== null && score < 2.5
			})
		},
		sortByDeliveryHigh() {
			this.suppliers = this.allSuppliers.filter((s) => {
				const score = this.getSortScore(s, "deliveryKpi")
				return score !== null && score > 3.5
			})
		},
		sortBySupportLow() {
			this.suppliers = this.allSuppliers.filter((s) => {
				const score = this.getSortScore(s, "supportKpi")
				return score !== null && score < 2.5
			})
		},
		sortBySupportHigh() {
			this.suppliers = this.allSuppliers.filter((s) => {
				const score = this.getSortScore(s, "supportKpi")
				return score !== null && score > 3.5
			})
		},
	},
}
</script>

<style scoped>
.supplier-performance-intro-card {
	border-radius: 8px;
	overflow: hidden;
}

.supplier-performance-intro-text {
	color: #424242;
	line-height: 1.6;
}

.supplier-performance-intro-hint {
	color: #1a1a1a;
	font-weight: 500;
}

.supplier-performance-sort {
	justify-content: flex-start;
	align-items: center;
}

.supplier-performance-sort-label {
	font-size: 1.25rem;
	font-weight: 700;
	color: #1a1a1a;
	white-space: nowrap;
	flex: 0 0 auto;
}

.supplier-performance-sort-select {
	max-width: 220px;
	width: 220px;
	flex: 0 0 auto;
}

.supplier-performance-sort-select >>> .v-input {
	flex: none;
	max-width: 220px;
}

.supplier-performance-table-card {
	background: #fff;
	border-radius: 8px;
	overflow: hidden;
}

.supplier-performance-search-title {
	width: 100%;
	padding: 20px 20px 8px;
	background: #fff;
}

.supplier-performance-search {
	width: 100%;
	margin-top: 0;
	padding-top: 0;
}

.supplier-performance-search >>> .v-input {
	width: 100%;
	padding-top: 0;
	margin-top: 0;
}

.supplier-performance-search >>> .v-input__control {
	width: 100%;
	min-height: 40px;
}

.supplier-performance-search >>> .v-input__slot {
	width: 100%;
	margin-bottom: 0;
}

.supplier-performance-search >>> .v-input__slot::before,
.supplier-performance-search >>> .v-input__slot::after {
	border-color: rgba(0, 0, 0, 0.24) !important;
}

.supplier-performance-search >>> .v-label {
	color: #757575 !important;
	font-size: 1rem;
}

.supplier-performance-search >>> input {
	padding-left: 0;
}

.supplier-performance-table >>> .v-data-table__wrapper {
	background: #fff;
}

.supplier-performance-table >>> .v-data-table-header tr th {
	font-size: 15px !important;
	color: #217b7f !important;
	font-weight: 600;
	border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
	text-transform: none;
	background: #fff !important;
	padding: 18px 16px !important;
}

.supplier-performance-table >>> .v-data-table-header tr th:first-child {
	padding-left: 20px !important;
}

.supplier-performance-table >>> .v-data-table-header tr th:not(:first-child) {
	padding-left: 24px !important;
}

.supplier-performance-table >>> tbody tr td {
	font-size: 14px;
	color: #424242;
	border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
	border-right: none !important;
	padding: 18px 16px !important;
	vertical-align: middle;
	background: #fff;
}

.supplier-performance-table >>> tbody tr td:first-child {
	padding-left: 20px !important;
}

.supplier-performance-table >>> tbody tr td:not(:first-child) {
	padding-left: 24px !important;
}

.supplier-performance-table >>> .v-data-footer {
	border-top: thin solid rgba(0, 0, 0, 0.12);
	font-size: 14px;
	color: #424242;
	background: #fff;
}

.supplier-performance-table >>> .v-data-footer__select .v-input {
	max-width: 56px;
	margin-top: 0;
	padding-top: 0;
}

.supplier-performance-table >>> .v-data-table__empty-wrapper {
	padding: 36px 16px;
	color: #757575;
	font-size: 14px;
}

.table-score-cell,
.table-center-cell {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
}

.table-score-cell--start,
.table-center-cell--start {
	justify-content: flex-start;
}

.supplier-link {
	color: #424242;
	font-weight: 400;
	text-decoration: none;
}

.supplier-link:hover {
	color: #1E7C83;
}

.average-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 52px;
	padding: 6px 14px;
	border-radius: 8px;
	border: 1.5px solid;
	font-weight: 700;
	font-size: 0.9375rem;
	text-align: center;
}

.score-empty {
	color: #757575;
	font-weight: 400;
}

.evaluation-date-cell {
	font-weight: 400;
	color: #424242;
}

.supplier-performance-table >>> tbody tr td .expiry-date--expired {
	color: #c62828;
	font-weight: 700;
}

.expiry-date--expired {
	color: #c62828;
	font-weight: 700;
}
</style>
